import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { Sidebar } from 'primeng/sidebar';

import { DropDownList } from '@core/interfaces';
import { ExportService, GridActions, GridService, ListService } from '@core/services';

@Component({
  selector: 'z-grid-export',
  templateUrl: './export.component.html',
  styleUrls: ['./export.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportComponent implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<void>();

  @Input() filename: string = 'export';
  @Input() exportData: (rows: number, format?: string) => any;

  @ViewChild('sidebar') sidebar: Sidebar;

  minRows = 0;
  maxRows = 25000;
  warningRowCount = 10000;
  step = 100;

  form: FormGroup;
  data: any;

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  fileFormatList: DropDownList = [];

  constructor(
    private store: Store,
    public gridService: GridService,
    public listService: ListService,
    public exportService: ExportService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit() {
    this.translocoService
      .selectTranslation()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.initializeStaticDropdownLists();
      });

    this.form = new FormGroup({
      format: new FormControl('xlsx'),
      rows: new FormControl(500),
      includeHighlights: new FormControl('Y'),
    });

    setTimeout(() => {
      this.form.get('format').setValue('xlsx');
    }, 0);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();

    if (this.sidebar?.mask) {
      this.sidebar.destroyModal();
    }
  }

  ngAfterViewInit() {
    // reset initial value after view init so the observable emits the initial value
    this.form.get('rows').setValue(this.form.get('rows').value);
  }

  onHide(_event: any) {
    this.reset();

    this.gridService.exportDataPanel$.pipe(take(1)).subscribe((state) => {
      if (state === 'open') {
        this.store.dispatch(GridActions.closeExportPanel());
      }
    });

    // workaround for primeng issue where mask is not destroyed
    if (this.sidebar.mask) {
      this.sidebar.destroyModal();
    }
  }

  export() {
    this.exportService.loading$.next(true);
    this.exportService.step$.next(this.translate('core.components.grid.export.requesting_data'));

    this.exportData(this.form.get('rows').value, this.form.get('format').value);

    const includeHighlights = this.form.get('includeHighlights').value === 'Y' ? true : false;

    this.exportService.data$
      .pipe(
        filter((x) => !!x),
        take(1)
      )
      .subscribe((data) => {
        this.exportService.export(data.columns, data.format, this.filename, includeHighlights, data.data);
      });
  }

  reset() {
    this.form.reset({
      format: 'xlsx',
      rows: 500,
      includeHighlights: 'Y',
    });
  }

  initializeStaticDropdownLists() {
    this.fileFormatList = [
      { column1: 'csv', column2: this.translate('core.components.grid.export.formats.csv') },
      { column1: 'json', column2: this.translate('core.components.grid.export.formats.json') },
      { column1: 'xlsx', column2: this.translate('core.components.grid.export.formats.xlsx') },
    ];
  }
}
